/**
 * TreeMapGridNode Component
 *
 * Individual node in the TreeMap grid. Positioned using CSS Grid properties.
 * Replaces TreeMapCard with grid-based positioning.
 */

import React, { useState, useEffect, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

/**
 * Capitalize the first letter of each word in a string
 */
function toTitleCase(str: string): string {
  return str.split(' ').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}
import type { GridNode } from '../../organisms/TreeMap/useTreeMapLayout';
import type { TreeMapTemplate, TemplateLevelDefinition } from '../../organisms/TreeMap/types';
import { resolveLevelConfig, VISIBLE_PROPERTIES_LIMIT_BY_VIEW, ROOT_COLUMN_LEVEL_ID } from '../../organisms/TreeMap/defaults';
import type { BaseViewConfig, TreeMapViewConfig } from 'types/contextView';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import type { SphereStatusValue } from '../sphere-avatar/sphere-avatar';
import { Skeleton } from 'primereact/skeleton';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import StatusPopoverMolecule from '../Atlas/StatusPopover/StatusPopoverMolecule';
import type { GlyphColor } from '../../../types/glyph';
import './TreeMapGridNode.scss';

/**
 * Basic sphere info for assignee lookup
 */
interface BasicSphereInfo {
  id: string;
  label: string;
  name: string;
  type: string;
  glyph: string;
  glyphColor?: GlyphColor;
  imageId: string;
}

export interface TreeMapGridNodeProps {
  node: GridNode;
  levelConfig?: TemplateLevelDefinition;
  template: TreeMapTemplate;
  editable?: boolean;
  /** Merged base display config; resolved per-column via `resolveLevelConfig` using `levelConfig.id`. */
  baseConfig: BaseViewConfig;
  /** Merged view config; resolved per-column via `resolveLevelConfig` using `levelConfig.id`. */
  viewConfig: TreeMapViewConfig;
  columnOffset?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoadingDetails?: boolean;
  isCollapsed?: boolean;
  hasChildren?: boolean;
  isNew?: boolean;
  isLastColumn?: boolean;
  onSelect?: () => void;
  onExpand?: () => void;
  onCreate?: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
  onToggleCollapse?: () => void;
  onLoadMore?: () => void;
  onFocus?: () => void;
  schemas?: any[];
  sphereLookup?: Record<string, BasicSphereInfo>;
}

const TreeMapGridNode: React.FC<TreeMapGridNodeProps> = ({
  node,
  levelConfig,
  template,
  editable = false,
  baseConfig,
  viewConfig,
  columnOffset = 2,
  hasMore = false,
  isLoadingMore = false,
  isLoadingDetails = false,
  isCollapsed = false,
  hasChildren = false,
  isNew = false,
  isLastColumn = false,
  onSelect,
  onExpand,
  onCreate,
  onUpdate,
  onDelete,
  onToggleCollapse,
  onFocus,
  onLoadMore,
  schemas,
  sphereLookup,
}) => {
  const { sphere, column, rowStart, rowSpan, colorIndex } = node;
  const [isHovered, setIsHovered] = useState(false);

  // Resolve this column's effective display config (level override → view/base config → defaults).
  // The focus/root column has no ContextLevel, so it always reads the '__root__' fallback chain.
  const resolvedConfig = resolveLevelConfig({ base: baseConfig, view: viewConfig }, levelConfig?.id ?? ROOT_COLUMN_LEVEL_ID);
  const cardView = resolvedConfig.cardDensity;
  const displayFields = resolvedConfig.visibleProperties;
  const { showStatus, showFlow, showAvatar } = resolvedConfig;

  // Ref for status popover
  const statusPopoverRef = useRef<OverlayPanel>(null);

  // Handle status sparkle click to open popover
  const handleStatusClick = (e: React.MouseEvent) => {
    console.log('[TreeMapGridNode] handleStatusClick called', {
      sphereId: sphere.id,
      hasRef: !!statusPopoverRef.current,
      event: e
    });
    e.stopPropagation(); // Don't trigger card selection
    statusPopoverRef.current?.toggle(e);
  };

  // Debug: Log edit icon conditions
  useEffect(() => {
    if (isHovered) {
      console.log(`🔍 [TreeMapGridNode] Hover state for "${sphere.properties.name}":`, {
        isHovered,
        editable,
        hasOnUpdate: !!onUpdate,
        shouldShowEditIcon: isHovered && editable && !!onUpdate,
      });
    }
  }, [isHovered, editable, onUpdate, sphere.properties.name]);

  // Get glyph and image from sphere
  const glyph = sphere.glyph ?? 'default-avatar';
  const glyphColor = sphere.glyphColor;
  const imageId = Array.isArray(sphere.properties.imageId)
    ? sphere.properties.imageId[0] || null
    : sphere.properties.imageId || null;

  // Render field value
  const renderFieldValue = (fieldName: string) => {
    const value = sphere.properties[fieldName];
    if (value === null || value === undefined) return null;

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Renders the author's ordered visibleProperties list, capped per density.
  // Uniform across all three densities — no field is special-cased as "the body".
  const renderVisibleProperties = () => {
    const limit = VISIBLE_PROPERTIES_LIMIT_BY_VIEW[cardView] ?? VISIBLE_PROPERTIES_LIMIT_BY_VIEW.standard;
    const rows = displayFields
      .slice(0, limit)
      .map((fieldName) => ({ fieldName, value: renderFieldValue(fieldName) }))
      .filter((row): row is { fieldName: string; value: string } => row.value !== null);

    if (rows.length === 0) return null;

    return (
      <div className="tree-map-grid-node__fields">
        {rows.map(({ fieldName, value }) => (
          <div key={fieldName} className="tree-map-grid-node__field">
            <span className="tree-map-grid-node__field-value">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Get urgency level color (CSS var) and label
  const getUrgencyInfo = () => {
    const urgency = sphere.properties.urgency;
    if (!urgency) return null;

    const urgencyMap: Record<string, { color: string; label: string }> = {
      'critical': { color: 'var(--danger-500)', label: 'Critical' },
      'high': { color: 'var(--caution-500)', label: 'High' },
      'medium': { color: 'var(--warning-500)', label: 'Medium' },
      'low': { color: 'var(--success-400)', label: 'Low' },
    };

    return urgencyMap[urgency.toLowerCase()] || { color: 'var(--surface-500)', label: urgency };
  };

  // Get workflow status if available
  const getWorkflowStatus = () => {
    // Check properties first
    const stageId = sphere.properties.currentStageId;
    const stageName = sphere.properties.currentStageName;
    if (stageName) return stageName;
    if (stageId) return `Stage ${stageId}`;

    // Check flow object for current stage
    if (sphere.flow?.currentStage) {
      return sphere.flow.currentStage.fallbackName || sphere.flow.currentStage.name || sphere.flow.currentStage.id;
    }

    // Check flow stages for in_progress stage
    if (sphere.flow?.stages) {
      const inProgressStage = sphere.flow.stages.find((s: any) => s.status === 'in_progress');
      if (inProgressStage) {
        return inProgressStage.fallbackName || inProgressStage.name || inProgressStage.id;
      }
    }

    return null;
  };

  // Get arc health data if available (expanded mode)
  const getArcHealth = () => {
    if (!sphere.flow?.stages || sphere.flow.stages.length === 0) {
      return null;
    }

    const stages = sphere.flow.stages;
    const completedStages = stages.filter((s: any) => s.status === 'completed').length;
    const inProgressStages = stages.filter((s: any) => s.status === 'in_progress').length;
    const notStartedStages = stages.filter((s: any) => s.status === 'not_started').length;

    // Calculate health percentage
    const healthPercent = Math.round((completedStages / stages.length) * 100);

    // Determine health color
    let healthColor = '#10b981'; // green for good health
    if (healthPercent < 30) {
      healthColor = '#ef4444'; // red for poor health
    } else if (healthPercent < 70) {
      healthColor = '#f97316'; // orange for moderate health
    }

    return {
      totalStages: stages.length,
      completed: completedStages,
      inProgress: inProgressStages,
      notStarted: notStartedStages,
      healthPercent,
      healthColor
    };
  };

  // Get sphere status point indicator (from sphere.status point)
  // Returns a SphereStatusValue for the SphereAvatar sparkle
  const getSphereStatus = (): SphereStatusValue => {
    const statusPoint = sphere.status;
    const statusValue = statusPoint?.currentValue?.status;

    if (!statusValue) {
      return 'unknown';
    }

    // Valid canonical status values (excluding lifecycle/non-canonical values)
    const validStatuses: SphereStatusValue[] = [
      'on_track', 'at_risk', 'needs_attention', 'critical', 'blocked', 'unknown'
    ];

    return validStatuses.includes(statusValue as SphereStatusValue)
      ? (statusValue as SphereStatusValue)
      : 'unknown';
  };

  // Get relationship perspectives - perspectives on edges FROM parent TO this child
  // These are the system names like 'center_parent', 'center_responsible', etc.
  const getRelationshipPerspectives = (limit?: number) => {
    const relationshipPerspectives = sphere.relationshipPerspectives || [];

    if (relationshipPerspectives.length === 0) return null;

    // Get perspective labels from center_abstract schema if available
    const getPerspectiveLabel = (systemName: string): string => {
      // Look for center_abstract schema which defines base perspectives
      const abstractSchema = schemas?.find((s: any) => s.system_name === 'center_abstract');
      if (abstractSchema?.perspectives) {
        const perspective = abstractSchema.perspectives.find((p: any) => p.system_name === systemName);
        if (perspective?.label || perspective?.name) {
          return perspective.label || perspective.name;
        }
      }

      // Fallback: format system_name as readable label
      return toTitleCase(systemName.replaceAll('center_', '').replaceAll('_', ' '));
    };

    const perspectiveNames = relationshipPerspectives.map((systemName: string) => getPerspectiveLabel(systemName));

    const items = limit ? perspectiveNames.slice(0, limit) : perspectiveNames;
    return items;
  };

  // Get assigned spheres from perspectives object
  const getAssignedSpheres = () => {
    const assignments: Array<{
      label: string;
      assignees: Array<{ id: string; name: string; glyph?: string; glyphColor?: GlyphColor; imageId?: string }>;
    }> = [];
    const perspectivesData = sphere.perspectives || {};

    // Get perspective label from center_abstract schema or fallback map
    const getPerspectiveLabel = (systemName: string): string | null => {
      // Look for center_abstract schema which defines base perspectives
      const abstractSchema = schemas?.find((s: any) => s.system_name === 'center_abstract');
      if (abstractSchema?.perspectives) {
        const perspective = abstractSchema.perspectives.find((p: any) => p.system_name === systemName);
        if (perspective?.label || perspective?.name) {
          return perspective.label || perspective.name;
        }
      }

      // Fallback labels for known perspectives
      const fallbackLabels: Record<string, string> = {
        'center_center': 'Center',
        'center_parent': 'Parent',
        'center_responsible': 'Responsible',
        'center_accountable': 'Accountable',
        'center_consulted': 'Consulted',
        'center_informed': 'Informed',
      };
      return fallbackLabels[systemName] || null;
    };

    // Lookup assignee info from sphereLookup
    const getAssigneeInfo = (assigneeId: string) => {
      const assignee = sphereLookup?.[assigneeId];
      if (assignee) {
        return {
          id: assigneeId,
          name: assignee.name || assigneeId,
          glyph: assignee.glyph,
          glyphColor: assignee.glyphColor,
          imageId: assignee.imageId
        };
      }
      return { id: assigneeId, name: assigneeId };
    };

    for (const [perspKey, assigneeIds] of Object.entries(perspectivesData)) {
      const label = getPerspectiveLabel(perspKey);
      if (label && Array.isArray(assigneeIds) && assigneeIds.length > 0) {
        const assignees = assigneeIds.map(id => getAssigneeInfo(id));
        assignments.push({ label, assignees });
      }
    }

    return assignments.length > 0 ? assignments : null;
  };

  // Get additional metadata fields for expanded view (excluding common/internal fields)
  const getAdditionalMetadata = () => {
    const excludeFields = new Set([
      'name', 'title', 'description', 'status', 'urgency', 'active',
      // Audit fields - already shown in audit info section
      'created_at', 'updated_at', 'created_by', 'updated_by',
      'createdAt', 'updatedAt', 'createdBy', 'updatedBy',
      'CreatedAt', 'UpdatedAt', 'CreatedBy', 'UpdatedBy',
      // Internal/system fields
      'currentStageId', 'currentStageName', 'imageId', 'type',
      'id', 'organization_id', 'schema_type', 'parent_id',
      'sphereId', 'schema_id', 'schemaId', 'sphere_id',
      'versionId', 'version_id', 'versionid',
      // Domain and other org fields
      'domain', 'Domain',
      ...displayFields // Also exclude fields already shown by displayFields
    ]);

    const metadata: Array<{ key: string; label: string; value: string }> = [];

    for (const [key, value] of Object.entries(sphere.properties)) {
      if (excludeFields.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;

      // Format the key as a label
      const label = toTitleCase(key.replaceAll('_', ' '));
      const displayValue = renderFieldValue(key);

      if (displayValue) {
        metadata.push({ key, label, value: displayValue });
      }
    }

    return metadata.length > 0 ? metadata.slice(0, 5) : null; // Limit to 5 fields
  };

  // Determine if we can expand (dynamic mode only and has potential children)
  const canExpand = template.mode === 'dynamic' && !node.isExpanded;

  // Build CSS classes for color coding
  const nodeClasses = [
    'tree-map-grid-node',
    `tree-map-grid-node--${cardView}`,
    `tree-map-grid-node--column-${column}`,
    `tree-map-grid-node--color-${colorIndex}`,
    column === 0 ? 'tree-map-grid-node--color-depth-0' : '',
    isHovered ? 'tree-map-grid-node--hovered' : '',
    isCollapsed ? 'tree-map-grid-node--collapsed' : '',
  ].filter(Boolean).join(' ');

  // Grid positioning - only apply if not in a children grid (columnOffset > 0)
  // Force rowSpan to 1 when collapsed to eliminate spacing
  const effectiveRowSpan = (isCollapsed && hasChildren) ? 1 : rowSpan;

  // Row height varies by detail level to accommodate different amounts of content
  // Only apply minHeight to leaf nodes (rowSpan === 1) to avoid forcing parent nodes to be too tall
  const ROW_HEIGHT_BY_VIEW = {
    minimal: 50,    // Compact view - header + a single one-line property
    standard: 160,  // Standard view - header + description + badges
    expanded: 280,  // Expanded view - header + description + badges + assignments + metadata
  };
  const ROW_HEIGHT = ROW_HEIGHT_BY_VIEW[cardView] || 160;

  // Only set minHeight for leaf nodes (single row span)
  // Parent nodes spanning multiple rows should size to their content/children
  const shouldApplyMinHeight = effectiveRowSpan === 1;

  // Grid positioning styles - only for main grid (columnOffset > 0)
  const gridPositionStyle = columnOffset > 0 ? {
    gridColumn: column + columnOffset,
    gridRow: `${rowStart} / span ${effectiveRowSpan}`,
  } : {};

  // MinHeight applies to all cards (both main grid and children grid)
  const minHeightStyle = shouldApplyMinHeight ? { minHeight: `${ROW_HEIGHT}px` } : {};

  return (
    <button
      type="button"
      className={nodeClasses}
      style={{ ...gridPositionStyle, ...minHeightStyle }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
    >
      <div className="tree-map-grid-node__content">
        {/* Top-right Add Button - hide on column 0 (focus), column 1 (subject), and last column (uses TreeMapChildrenGrid) */}
        {editable && onCreate && column > 1 && !isLastColumn && (
          <button
            type="button"
            className="tree-map-grid-node__add-button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onCreate();
            }}
            title="Add related item"
            aria-label="Add related item"
          >
            <i className="pi pi-plus" />
          </button>
        )}

        {/* Header */}
        <div className="tree-map-grid-node__header">
          <div className="tree-map-grid-node__title">
            {showAvatar && (
              <SphereAvatar
                glyph={glyph}
                glyphColor={glyphColor}
                sphereId={sphere.id}
                imageId={imageId}
                size="small"
                className="tree-map-grid-node__avatar"
                status={showStatus ? getSphereStatus() : undefined}
                onStatusClick={showStatus ? handleStatusClick : undefined}
              />
            )}
            <span className="tree-map-grid-node__name">
              {sphere.properties.name || sphere.properties.title || 'Untitled'}
              {isNew && (
                <i
                  className="pi pi-star-fill tree-map-grid-node__new-indicator"
                  style={{ color: '#fbbf24', marginLeft: '6px', fontSize: '14px' }}
                  title="Newly created"
                />
              )}
            </span>
          </div>

          {/* Actions - always show eye icon when collapsed, otherwise show on hover */}
          {(isHovered || (isCollapsed && hasChildren)) && (
            <div className="tree-map-grid-node__actions">
              {isHovered && onFocus && (
                <ButtonAtom
                  icon="pi pi-bullseye"
                  variant="secondary"
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onFocus();
                  }}
                  tooltip="Focus on this sphere"
                  aria-label="Focus on this sphere"
                />
              )}
              {isHovered && editable && onUpdate && (
                <ButtonAtom
                  icon="pi pi-pencil"
                  variant="secondary"
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onUpdate();
                  }}
                  tooltip="Edit sphere"
                  aria-label="Edit sphere"
                />
              )}
              {hasChildren && (
                <ButtonAtom
                  icon={isCollapsed ? "pi pi-eye-slash" : "pi pi-eye"}
                  variant="secondary"
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onToggleCollapse?.();
                  }}
                  tooltip={isCollapsed ? "Show children" : "Hide children"}
                  aria-label={isCollapsed ? "Show children" : "Hide children"}
                />
              )}
              {isHovered && editable && canExpand && (
                <ButtonAtom
                  icon="pi pi-arrow-right"
                  variant="secondary"
                  size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onExpand?.();
                  }}
                  tooltip="Expand"
                  aria-label="Expand"
                />
              )}
            </div>
          )}
        </div>

        {/* Body - the ordered visibleProperties list renders at every density,
            capped by VISIBLE_PROPERTIES_LIMIT_BY_VIEW; no field is special-cased. */}
        {!isCollapsed && (
          <div className="tree-map-grid-node__body">
            {renderVisibleProperties()}

            {/* Standard View: status indicators */}
            {cardView === 'standard' && (
              <>
                {/* Status badges row */}
                <div className="tree-map-grid-node__badges">
                  {/* Urgency badge */}
                  {showStatus && getUrgencyInfo() && (
                    <span
                      className="tree-map-grid-node__badge"
                      style={{
                        backgroundColor: getUrgencyInfo()!.color,
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {getUrgencyInfo()!.label}
                    </span>
                  )}

                  {/* Workflow status badge */}
                  {showFlow && getWorkflowStatus() && (
                    <span
                      className="tree-map-grid-node__badge"
                      style={{
                        backgroundColor: 'var(--info-500)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {getWorkflowStatus()}
                    </span>
                  )}

                </div>

                {/* Relationship perspectives (limit to 2) */}
                {getRelationshipPerspectives(2) && (
                  <div className="tree-map-grid-node__perspectives">
                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>Relationship:</span>
                    {getRelationshipPerspectives(2)!.map((name: string) => (
                      <span
                        key={`perspective-${name}`}
                        className="tree-map-grid-node__perspective-badge"
                        style={{
                          backgroundColor: 'var(--surface-100)',
                          color: 'var(--text-secondary)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                        }}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Center perspective assignment (standard view) */}
                {getAssignedSpheres()?.filter(a => a.label === 'Center').map((assignment, assignmentIdx) => (
                  <div key={`assignment-${assignment.label}-${assignmentIdx}`} className="tree-map-grid-node__assignment-row" style={{ marginTop: '4px' }}>
                    <span className="tree-map-grid-node__assignment-label">{assignment.label}:</span>
                    <div className="tree-map-grid-node__assignment-values">
                      {assignment.assignees.slice(0, 2).map((assignee, assigneeIdx) => (
                        <SphereIdentityAtom
                          // The same assignee can appear under multiple relationships —
                          // disambiguate the key by index to avoid collisions.
                          key={`${assignee.id}-${assigneeIdx}`}
                          sphereId={assignee.id}
                          sphereName={assignee.name}
                          glyph={assignee.glyph}
                          glyphColor={assignee.glyphColor}
                          imageId={assignee.imageId}
                          size="xsmall"
                          className="tree-map-grid-node__assignee"
                        />
                      ))}
                      {assignment.assignees.length > 2 && (
                        <span className="tree-map-grid-node__more-assignees">
                          +{assignment.assignees.length - 2} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Expanded View: All standard content + additional details */}
            {cardView === 'expanded' && (
              <>
                {/* Skeleton loading for expanded details */}
                {isLoadingDetails && (
                  <div className="tree-map-grid-node__skeleton-details">
                    <div style={{ marginBottom: '8px' }}>
                      <Skeleton width="60%" height="12px" className="mb-2" />
                      <Skeleton width="80%" height="12px" className="mb-2" />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <Skeleton width="80px" height="20px" borderRadius="10px" />
                      <Skeleton width="60px" height="20px" borderRadius="10px" />
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Skeleton width="100px" height="10px" className="mb-1" />
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Skeleton shape="circle" size="14px" />
                        <Skeleton width="80px" height="10px" />
                      </div>
                    </div>
                    <div>
                      <Skeleton width="100px" height="10px" className="mb-1" />
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Skeleton shape="circle" size="14px" />
                        <Skeleton width="70px" height="10px" />
                      </div>
                    </div>
                  </div>
                )}

                {!isLoadingDetails && (
                  <>

                {/* Status badges row */}
                <div className="tree-map-grid-node__badges">
                  {showStatus && getUrgencyInfo() && (
                    <span
                      className="tree-map-grid-node__badge"
                      style={{
                        backgroundColor: getUrgencyInfo()!.color,
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {getUrgencyInfo()!.label}
                    </span>
                  )}

                  {showFlow && getWorkflowStatus() && (
                    <span
                      className="tree-map-grid-node__badge"
                      style={{
                        backgroundColor: 'var(--info-500)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {getWorkflowStatus()}
                    </span>
                  )}

                </div>

                {/* Full relationship perspectives */}
                {getRelationshipPerspectives() && (
                  <div className="tree-map-grid-node__perspectives">
                    <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>Relationship:</span>
                    {getRelationshipPerspectives()!.map((name: string) => (
                      <span
                        key={`exp-perspective-${name}`}
                        className="tree-map-grid-node__perspective-badge"
                        style={{
                          backgroundColor: 'var(--surface-100)',
                          color: 'var(--text-secondary)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                        }}
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Assigned spheres (Perspective Assignments) */}
                {getAssignedSpheres() && (
                  <div className="tree-map-grid-node__assignments-section">
                    <div className="tree-map-grid-node__section-header" style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Assignments
                    </div>
                    <div className="tree-map-grid-node__assignments">
                      {getAssignedSpheres()!.map((assignment, assignmentIdx) => (
                        <div key={`exp-assignment-${assignment.label}-${assignmentIdx}`} className="tree-map-grid-node__assignment-row">
                          <span className="tree-map-grid-node__assignment-label">{assignment.label}:</span>
                          <div className="tree-map-grid-node__assignment-values">
                            {assignment.assignees.slice(0, 2).map((assignee, assigneeIdx) => (
                              <SphereIdentityAtom
                                key={`${assignee.id}-${assigneeIdx}`}
                                sphereId={assignee.id}
                                sphereName={assignee.name}
                                glyph={assignee.glyph}
                                glyphColor={assignee.glyphColor}
                                imageId={assignee.imageId}
                                size="xsmall"
                                className="tree-map-grid-node__assignee"
                              />
                            ))}
                            {assignment.assignees.length > 2 && (
                              <span className="tree-map-grid-node__more-assignees">
                                +{assignment.assignees.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional sphere properties not in displayFields */}
                {getAdditionalMetadata() && (
                  <div className="tree-map-grid-node__extra-metadata" style={{ marginTop: '6px' }}>
                    {getAdditionalMetadata()!.map(({ key, label, value }) => (
                      <div key={key} style={{ fontSize: '10px', color: '#6b7280' }}>
                        <span style={{ fontWeight: 500 }}>{label}:</span> {value}
                      </div>
                    ))}
                  </div>
                )}

                {/* Arc Health (if available) */}
                {showFlow && getArcHealth() && (
                  <div className="tree-map-grid-node__flow-health" style={{ marginTop: '8px', padding: '6px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500, marginBottom: '4px' }}>Flow Progress:</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${getArcHealth()!.healthPercent}%`,
                            height: '100%',
                            backgroundColor: getArcHealth()!.healthColor,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: getArcHealth()!.healthColor }}>
                        {getArcHealth()!.healthPercent}%
                      </span>
                    </div>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>
                      {getArcHealth()!.completed} completed • {getArcHealth()!.inProgress} in progress • {getArcHealth()!.notStarted} not started
                    </div>
                  </div>
                )}

                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Status Popover */}
      <StatusPopoverMolecule
        ref={statusPopoverRef}
        sphereId={sphere.id}
        sphereName={sphere.properties.name || sphere.properties.title || 'Untitled'}
        schemaType={sphere.properties?.type}
        status={sphere.status}
      />
    </button>
  );
};

export default TreeMapGridNode;
